import { AxiosResponse } from 'axios';
import React, { createRef, useState } from 'react';
import { Container, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { reactFormatter } from 'react-tabulator';
import Loader from '../../common-components/loader';
import TableView from '../../Components/tableView';
import constants from '../../constants';
import { ModifyBusinessNameAndVat } from '../../Components/Contacts/ModifyBusinessNameAndVat';
import { ModifyContactProperties } from '../../Components/Contacts/ModifyContactProperties';
import Contact, { ContactInfoResponse } from '../../Model/contact';
import { CompaniesList } from '../../Components/Contacts/CompaniesList';
import Company from '../../Model/company';
import { DateTime } from 'luxon';
import './contacts.css';
import ContactActions from '../../Components/Contacts/ContactActions';
import FormattedContactInfo from '../../Components/Contacts/FormattedContactInfo';
import { banEmail, getContact } from '../../api/contacts';
import CustomAlert, { CustomAlertType } from '../../Components/CustomAlert';

const Contacts = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [openModalBusinessAndVat, setOpenModalBusinessAndVat] =
    useState<boolean>(false);
  const [rowData, setRowData] = useState<any>({});
  const [openModalContact, setOpenModalContact] = useState<boolean>(false);
  const [openModalContactCompanies, setOpenModalContactCompanies] =
    useState<boolean>(false);
  const [openModalContactInfo, setOpenModalContactInfo] =
    useState<boolean>(false);
  const [loadingContactInfo, setLoadingContactInfo] = useState<boolean>(false);
  const [contactInfo, setContactInfo] = useState<ContactInfoResponse>();
  const [currentCompany, setCurrentCompany] = useState<Company>();
  const tabulator = createRef<TableView>();
  const [showError, setShowError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [errorType, setErrorType] = useState<CustomAlertType>();

  const { t } = useTranslation();


  const banMail = async (rowDataValue: Record<string, unknown>) => {
    const mail = rowDataValue.mail as string;
    const check = window.confirm(t('CONFIRM_MAIL_BAN', { mail }));
    if (check) {
      setLoading(true);
      try {
        const result: AxiosResponse = await banEmail(mail);
        if (result.data.success === true) {
          setShowError(true);
          setErrorType(CustomAlertType.INFO);
          setErrorMessage(t('CONTACT_BANNED'));
        }
      } catch (error: any) {
        setShowError(true);
        setErrorType(CustomAlertType.ERROR);
        setErrorMessage(error.response.data.errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  const formatBan = (cell: any) => {
    const data = cell.getData();
    return data?.is_banned ? t('YES') : t('NO');
  };

  const KYBformatter = (cell: any) => {
    const data = cell.getData();
    switch (data.tier_KYB) {
      case -1:
        return t('NOT_ELIGIBLE');
      case 0:
        return 0;
      case 1:
        return 1;
      default:
        return t('NOT_STARTED');
    }
  };

  const onOpenModalBusinessAndVat = () => {
    setOpenModalBusinessAndVat(!openModalBusinessAndVat);
  };

  const onOpenModalContact = (contact: Contact) => {
    setOpenModalContact(!openModalContact);
    setRowData(contact);
  };

  const onOpenModalContactCompanies = (contact: Contact) => {
    setOpenModalContactCompanies(!openModalContactCompanies);
    setRowData(contact);
  };

  const onOpenModalLocalization = () => {
    setOpenModalBusinessAndVat(!openModalBusinessAndVat);
  };

  const onOpenModalContactInfo = async (cellData: any) => {
    setOpenModalContactInfo(!openModalContactInfo);
    setLoadingContactInfo(true);
    const dataFromAPI = await getContact(cellData.id);
    if (dataFromAPI.data) {
      setContactInfo(dataFromAPI.data.data as ContactInfoResponse);
      setLoadingContactInfo(false);
    }
  };

  const getRowData = (cell: any) => {
    setRowData(cell);
  };

  const closeModal = () => setOpenModalContactInfo(false);

  const openCustomAlert = (message: string, type: CustomAlertType, show: boolean) => {
    setErrorMessage(message);
    setErrorType(type);
    setShowError(show);
  };

  const formatDate = (date?: string) => {
    if (date !== undefined && date !== null) {
      return DateTime.fromISO(date).toLocaleString();
    }
    return '-';
  };

  /* eslint-disable quote-props */
  const filterSelectOptions = { '1': t('YES'), '0': t('NO'), '': t('ALL') };
  /* eslint-enable i18next/no-literal-string */
  const columnsPersonal = [
    { title: t('ID'), field: 'id', visible: false },
    {
      title: t('SURNAME'),
      width: 150,
      field: 'surname',
      sorter: 'string',
      headerFilter: 'input'
    },
    {
      title: t('NAME'),
      width: 150,
      field: 'name',
      sorter: 'string',
      headerFilter: 'input'
    },
    {
      title: t('MAIL'),
      width: 250,
      field: 'mail',
      sorter: 'string',
      headerFilter: 'input'
    },
    {
      title: t('BUSINESS_NAME'),
      field: 'business_name',
      sorter: 'string',
      width: 200,
      headerFilter: 'input'
    },
    {
      title: t('VAT'),
      field: 'vat',
      sorter: 'string',
      width: 100,
      headerFilter: 'input'
    },
    {
      title: t('KYC'),
      field: 'tier',
      sorter: 'string',
      width: 100,
      headerFilter: 'input'
    },
    {
      title: t('KYB'),
      field: 'tier_KYB',
      sorter: 'string',
      width: 100,
      headerFilter: 'input',
      formatter: KYBformatter
    },
    {
      title: t('BAN'),
      field: 'is_banned',
      sorter: 'string',
      headerFilter: 'select',
      editorParams: filterSelectOptions,
      headerFilterParams: filterSelectOptions,
      width: 80,
      formatter: formatBan
    },
    {
      title: '',
      sortable: false,
      formatter: reactFormatter(
        <ContactActions
          props={{
            banMail: banMail,
            t: t,
            tabulator: tabulator,
            openModalBusinessAndVat: onOpenModalBusinessAndVat,
            openModalLocalization: onOpenModalLocalization,
            openModalContact: onOpenModalContact,
            getRowData: getRowData,
            openModalContactCompanies: onOpenModalContactCompanies,
            openModalContactInfo: onOpenModalContactInfo,
            openCustomAlert: openCustomAlert
          }}
        />
      ),
      headerSort: false,
      width: 300,
      widthShrink: 0
    }
  ];

  return (
    <>
      <Container>
        <Dialog
        fullWidth
        className='dialog'
          open={openModalContactInfo}
          onClose={closeModal}
        >
          <DialogTitle className='dialogTitle'>
            {t('CONTACT_INFO')}
          </DialogTitle>
          <DialogContent style={{ width: 800 }}>
              {loadingContactInfo && <p>{t('LOADING')}</p>}
              {!loadingContactInfo && (
                <FormattedContactInfo
                  contactInfo={contactInfo}
                  filterSelectOptions={filterSelectOptions}
                  formatDate={(value: any) => formatDate(value)}
                  tabulator={tabulator}
                />
              )}
          </DialogContent>
        </Dialog>
        <Loader visible={loading} />
        <ModifyBusinessNameAndVat
        setShowError={(e: boolean) => setShowError(e)}
        setErrorMessage={(e: string) => setErrorMessage(e)}
        setErrorType={(e: any) => setErrorType(e)}
          business_id={currentCompany?.id}
          showModal={openModalBusinessAndVat}
          hideAllModal={() => {
            // setOpenModalContactCompanies(false);
            setOpenModalBusinessAndVat(false);
          }}
          hideModal={() => setOpenModalBusinessAndVat(false) }
          business_name={currentCompany?.business_name}
          vat={currentCompany?.vat}
          contact_id={rowData?.id}
        />

        <ModifyContactProperties
        setShowError={(e: boolean) => setShowError(e)}
        setErrorMessage={(e: string) => setErrorMessage(e)}
        setErrorType={(e: any) => setErrorType(e)}
          contact={rowData}
          showModal={openModalContact}
          hideModal={() => {
            setOpenModalContact(false);
            setCurrentCompany(undefined);
          }}
        />

        <CompaniesList
          contact={rowData}
          showModal={openModalContactCompanies}
          hideModal={() => setOpenModalContactCompanies(false)}
          selectCompany={(company: Company) => {
            setCurrentCompany(company);
            setOpenModalBusinessAndVat(company ? true : false);
          }}
        />
          <TableView
            t={t}
            ref={tabulator}
            api={constants.api.getContacts}
            columns={columnsPersonal}
            id={'contacts-list'}
          />
            <CustomAlert
          type={errorType}
          show={showError}
          message={errorMessage}
          onClose={() => { setShowError(false); setErrorMessage('');}} />
      </Container>
    </>
  );
};

export default Contacts;
